import { Component, Output, EventEmitter, OnInit, signal, inject, effect, input, DestroyRef } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyTableModule, GalaxyColumnDef, GalaxyDataSource } from '@vendasta/galaxy/table';
import {
  PaginatedAPIInterface,
  PagedListRequestInterface,
  PagedResponseInterface,
} from '@vendasta/galaxy/table/src/datasource/datasource.interface';
import { EmailLabelingConfigService } from './email-labeling-config.service';
import { MessageCategoryConfig } from './models';
import { Observable, of, EMPTY } from 'rxjs';
import { map, tap, catchError, finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

class ConfigListDataService implements PaginatedAPIInterface<MessageCategoryConfig, unknown, unknown> {
  constructor(private configs$: Observable<MessageCategoryConfig[]>) {}

  get(
    _request: PagedListRequestInterface<unknown, unknown>,
  ): Observable<PagedResponseInterface<MessageCategoryConfig>> {
    return this.configs$.pipe(
      map((configs) => ({
        data: configs,
        pagingMetadata: {
          nextCursor: '',
          hasMore: false,
          totalResults: configs.length,
        },
      })),
    );
  }
}

@Component({
  selector: 'email-labeling-config-list',
  standalone: true,
  imports: [
    TranslateModule,
    MatButtonModule,
    MatTableModule,
    MatIconModule,
    MatMenuModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
    GalaxyTableModule,
  ],
  templateUrl: './email-labeling-config-list.component.html',
  styleUrl: './email-labeling-config-list.component.scss',
})
export class EmailLabelingConfigListComponent implements OnInit {
  readonly namespace = input.required<string>();
  readonly userId = input.required<string>();
  readonly reloadTrigger = input<number>(0);

  @Output() addNew = new EventEmitter<void>();
  @Output() edit = new EventEmitter<MessageCategoryConfig>();
  @Output() delete = new EventEmitter<MessageCategoryConfig>();

  protected readonly configs = signal<MessageCategoryConfig[]>([]);
  protected readonly loading = signal<boolean>(true);

  protected columns: GalaxyColumnDef[] = [];

  protected dataSource!: GalaxyDataSource<MessageCategoryConfig, unknown, unknown>;

  private readonly service = inject(EmailLabelingConfigService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const configs = this.configs();
      if (configs.length > 0) {
        const dataService = new ConfigListDataService(of(configs));
        this.dataSource = new GalaxyDataSource<MessageCategoryConfig, unknown, unknown>(dataService);
      }
    });

    effect(() => {
      const trigger = this.reloadTrigger();
      const namespace = this.namespace();
      if (trigger > 0 && namespace) {
        this.loadConfigs();
      }
    });
  }

  ngOnInit(): void {
    this.columns = [
      { id: 'name', title: this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.CONFIG_NAME') },
      { id: 'categories', title: this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.LABELS') },
      { id: 'updated', title: this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.LAST_UPDATED') },
      { id: 'actions', title: '' },
    ];

    const namespace = this.namespace();
    if (namespace) {
      this.loadConfigs();
    }
  }

  private loadConfigs(): void {
    this.loading.set(true);

    this.service
      .listConfigs(this.namespace())
      .pipe(
        tap((configs) => this.configs.set(configs)),
        catchError(() => {
          this.configs.set([]);
          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected formatDate(date?: Date): string {
    if (!date) return '-';
    if (isNaN(date.getTime())) return 'Invalid Date';
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }

  protected onAddNew(): void {
    this.addNew.emit();
  }

  protected onEdit(config: MessageCategoryConfig): void {
    this.edit.emit(config);
  }

  protected onDelete(config: MessageCategoryConfig): void {
    this.delete.emit(config);
  }
}
